/**
 * The maroon bar with the gold rule — the app's `Header`, widened for a site.
 */
import { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GompaMark from './GompaMark';
import Icon from './Icon';
import LanguageSelector from './LanguageSelector';
import { useBooking } from '../context/BookingContext';
import { useDonorSession } from '../lib/useDonorSession';

export default function SiteHeader() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { savedIds } = useBooking();
  const { user } = useDonorSession();
  const location = useLocation();

  const links = [
    { to: '/explore', label: t('nav.explore', 'Explore'), icon: 'compass' },
    { to: '/offerings', label: t('nav.myOfferings', 'My Offerings'), icon: 'flame' },
    { to: '/saved', label: t('nav.saved', 'My Monasteries'), icon: 'heart' },
    { to: '/about', label: t('nav.about', 'About'), icon: 'journal' },
  ];

  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <header className="site-header">
      <div className="shell site-header__inner">
        <Link to="/" className="brand-lockup">
          <GompaMark size={38} />
          <span>
            <span className="brand-lockup__name">GOMPA</span>
            <span className="brand-lockup__strap">{t('nav.brandStrap', 'Monastery & Ritual Services')}</span>
          </span>
          <span className="visually-hidden"> — {t('nav.homeAria', 'home')}</span>
        </Link>

        {/* Action group on header: Language Selector directly to the left of hamburger on mobile */}
        <div className="site-header__actions">
          <LanguageSelector />

          <button
            type="button"
            className="site-header__menu-btn"
            aria-expanded={menuOpen}
            aria-controls="site-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <Icon name={menuOpen ? 'close' : 'menu'} size={22} />
            <span className="visually-hidden">
              {menuOpen ? t('nav.closeMenu', 'Close menu') : t('nav.openMenu', 'Open menu')}
            </span>
          </button>
        </div>

        <nav
          id="site-nav"
          className="site-header__nav"
          aria-label={t('nav.mainNav', 'Main')}
          data-open={menuOpen}
        >
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `site-header__link${isActive ? ' is-active' : ''}`
              }
            >
              <Icon name={link.icon} size={16} />
              {link.label}
              {link.to === '/saved' && savedIds.length > 0 && (
                <span className="site-header__count">{savedIds.length}</span>
              )}
            </NavLink>
          ))}

          <NavLink
            to="/account"
            className={({ isActive }) => `site-header__link${isActive ? ' is-active' : ''}`}
          >
            <Icon name="people" size={16} />
            {user ? (user.name ?? user.email.split('@')[0]) : t('nav.signIn', 'Sign in')}
          </NavLink>
        </nav>
      </div>
    </header>
  );
}
